import React, { useState, useEffect } from 'react';
import { Button, Form, InputGroup, Spinner, Dropdown } from 'react-bootstrap';
import { FaPercent, FaDollarSign, FaCheck, FaX, FaTag, FaPen, FaGlobe, FaUtensils, FaList } from 'react-icons/fa6';
import { updateDiscountForReceipt, updatePaymentReceiptSuggestedTip } from '../../../../../../../../../../../crud/payments_api/payment_receipts';
import { getManualDiscountsByLocalId } from '../../../../../../../../../../../crud/payments_api/payment_discounts';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../../../../../../../../../../utils/functions';
import MiniLoadingSpinner from '../../../../../../../../../../../components/Spinners/MiniLoadingSpinner';
import './DiscountSection.scss';

interface ManualDiscount {
  payment_discount_id: number;
  payment_discount_name: string;
  payment_discount_percentage?: number;
  payment_discount_amount?: number;
  scope_type: string;
  isglobal?: boolean;
  items_detail?: Array<{ item_id: number; item_name: string }>;
  categories_detail?: Array<{ category_id: number; category_name: string }>;
  menu_detail?: { menu_id: number; menu_name: string } | null;
}

interface DiscountSectionProps {
  receiptId: number;
  totalCuenta: number;
  discountAmount: number;
  setDiscountAmount: (discountAmount: number) => void;
  receipts: any[];
  setReceipts: any;
  handleClose: () => void;
  manualDiscountAmount?: number;
  setManualDiscountAmount?: (amount: number) => void;
  setTipAmount?: (amount: number) => void;
}

const DiscountSection: React.FC<DiscountSectionProps> = ({
  receiptId,
  totalCuenta,
  discountAmount,
  setDiscountAmount,
  receipts,
  setReceipts,
  handleClose,
  manualDiscountAmount = 0,
  setManualDiscountAmount,
  setTipAmount,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingDiscounts, setLoadingDiscounts] = useState(true);

  // Available manual discounts from API
  const [manualDiscounts, setManualDiscounts] = useState<ManualDiscount[]>([]);
  
  // Mode: 'preset' for selecting existing discount, 'custom' for manual entry
  const [mode, setMode] = useState<'preset' | 'custom'>('preset');
  
  // Selected preset discount
  const [selectedDiscount, setSelectedDiscount] = useState<ManualDiscount | null>(null);

  // Custom discount logic
  const [discountType, setDiscountType] = useState<'percentage' | 'amount'>('percentage');
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [discountRawAmount, setDiscountRawAmount] = useState<number>(0);
  const [comment, setComment] = useState<string>('');

  // ========== FETCH MANUAL DISCOUNTS ==========
  useEffect(() => {
    const fetchDiscounts = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') ?? '{}');
        const localId = user.local_id ? Number(user.local_id) : 0;
        
        if (localId > 0) {
          const response = await getManualDiscountsByLocalId(localId);
          const discounts = (response as any)?.data || [];
          setManualDiscounts(discounts);
          
          // If no preset discounts available, switch to custom mode
          if (discounts.length === 0) {
            setMode('custom');
          }
        } else {
          setMode('custom');
        }
      } catch (error) {
        console.error('Error fetching manual discounts:', error);
        setMode('custom');
      } finally {
        setLoadingDiscounts(false);
      }
    };

    fetchDiscounts();
  }, []);

  // ========== HELPERS ==========
  const getScopeDescription = (discount: ManualDiscount): string => {
    if (discount.isglobal || discount.scope_type === 'global') {
      return 'Todos los items';
    }
    if ((discount.scope_type === 'item' || discount.scope_type === 'multi_item') && discount.items_detail?.length) {
      return discount.items_detail.map(i => i.item_name).join(', ');
    }
    if ((discount.scope_type === 'category' || discount.scope_type === 'multi_category') && discount.categories_detail?.length) {
      return discount.categories_detail.map(c => c.category_name).join(', ');
    }
    if (discount.scope_type === 'menu' && discount.menu_detail) {
      return `Menú: ${discount.menu_detail.menu_name}`;
    }
    return 'Específico';
  };

  const getScopeIcon = (discount: ManualDiscount) => {
    if (discount.isglobal || discount.scope_type === 'global') {
      return <FaGlobe className="scope-icon global" />;
    }
    if (discount.scope_type === 'item' || discount.scope_type === 'multi_item') {
      return <FaUtensils className="scope-icon item" />;
    }
    if (discount.scope_type === 'category' || discount.scope_type === 'multi_category') {
      return <FaList className="scope-icon category" />;
    }
    return <FaTag className="scope-icon" />;
  };

  const getDiscountValue = (discount: ManualDiscount): string => {
    if (discount.payment_discount_percentage) {
      return `${discount.payment_discount_percentage}%`;
    }
    if (discount.payment_discount_amount) {
      return formatCurrency(discount.payment_discount_amount);
    }
    return '';
  };

  // ========== APPLY DISCOUNT LOGIC ==========
  const applyDiscountToReceipt = async () => {
    if (loading) return;

    // Validate based on mode
    if (mode === 'preset') {
      if (!selectedDiscount) {
        toast.error('Debes seleccionar un descuento');
        return;
      }
    } else {
      // Custom mode validation
      if (!comment.trim()) {
        toast.error('Debes agregar un comentario explicando el motivo del descuento');
        return;
      }
    }

    setLoading(true);

    try {
      let response;
      let calculatedDiscount = 0;

      if (mode === 'preset' && selectedDiscount) {
        // Apply preset discount using discount_id
        response = await updateDiscountForReceipt(
          receiptId,
          undefined, // No discount_amount
          comment.trim() || undefined,
          selectedDiscount.payment_discount_id
        );

        // Check for warning response (discount not applicable)
        const responseData = (response as any)?.data;
        if (responseData?.warning) {
          toast.warning(responseData.message || 'El descuento no se aplicó porque no hay items aplicables');
          setLoading(false);
          return;
        }

        // Get the applied discount amount from response
        if (responseData?.applied_discount?.amount) {
          calculatedDiscount = responseData.applied_discount.amount;
        } else if (responseData?.discount_amount !== undefined) {
          // Calculate the difference
          calculatedDiscount = responseData.discount_amount - discountAmount;
        }
      } else {
        // Custom discount logic
        if (discountType === 'percentage') {
          if (discountPercentage < 0 || discountPercentage > 100) {
            toast.error('El porcentaje debe estar entre 0 y 100.');
            setLoading(false);
            return;
          }
          calculatedDiscount = (discountPercentage / 100) * totalCuenta;
        } else {
          if (discountRawAmount < 0 || discountRawAmount > totalCuenta) {
            toast.error('El monto debe estar entre 0 y el total de la cuenta.');
            setLoading(false);
            return;
          }
          calculatedDiscount = discountRawAmount;
        }

        response = await updateDiscountForReceipt(
          receiptId,
          calculatedDiscount,
          comment.trim()
        );
      }

      const newTotalDiscount = calculatedDiscount + discountAmount;
      const newManualDiscount = calculatedDiscount + (manualDiscountAmount || 0);

      setDiscountAmount(newTotalDiscount);
      if (setManualDiscountAmount) {
        setManualDiscountAmount(newManualDiscount);
      }

      // If discount reaches 100% or more, reset tip to 0
      if (newTotalDiscount >= totalCuenta && setTipAmount) {
        setTipAmount(0);
      }

      setDiscountRawAmount(0);
      setDiscountPercentage(0);
      setSelectedDiscount(null);

      // Find current receipt to get its tip information
      const currentReceipt = receipts.find((r: any) => r.receipt_id === receiptId);

      // Recalculate tip proportionally after discount
      let newSuggestedTip = 0;
      if (currentReceipt && currentReceipt.suggested_tip > 0) {
        const oldNetBase = totalCuenta - (currentReceipt.coupon_amount || 0) - discountAmount;
        const tipRate = oldNetBase > 0 ? currentReceipt.suggested_tip / oldNetBase : 0;
        const newNetBase = totalCuenta - (currentReceipt.coupon_amount || 0) - newTotalDiscount;
        // Set tip to 0 if net base becomes 0 or negative (e.g., 100% discount)
        newSuggestedTip = newNetBase > 0 ? Math.round(newNetBase * tipRate) : 0;

        try {
          await updatePaymentReceiptSuggestedTip(receiptId, newSuggestedTip);
        } catch (error) {
          console.error('Error updating tip after discount:', error);
        }
      }

      const updatedReceipts = receipts.map((r: any) => {
        if (r.receipt_id === receiptId) {
          return {
            ...r,
            discount_amount: newTotalDiscount,
            manual_discount_amount: newManualDiscount,
            suggested_tip: newSuggestedTip,
          };
        }
        return r;
      });
      setReceipts(updatedReceipts);

      const successMessage = mode === 'preset' && selectedDiscount
        ? `Descuento "${selectedDiscount.payment_discount_name}" aplicado`
        : 'Descuento aplicado';
      toast.success(successMessage);

      setLoading(false);
      handleClose();
    } catch (error: any) {
      console.error('Error applying discount:', error);
      const errorMessage = error?.response?.data?.message || 'Error aplicando descuento';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  // ========== RENDERING ==========
  if (loadingDiscounts) {
    return (
      <div className="discount-panel loading-state">
        <Spinner animation="border" variant="warning" size="sm" />
        <span>Cargando descuentos...</span>
      </div>
    );
  }

  return (
    <>
      <div className="discount-panel">
        {/* Mode Toggle - Only show if there are preset discounts */}
        {manualDiscounts.length > 0 && (
          <div className="discount-mode-toggle">
            <Button
              variant="none"
              className={`mode-button ${mode === 'preset' ? 'active' : ''}`}
              onClick={() => setMode('preset')}
              disabled={loading}
            >
              <FaTag />
              Descuentos predefinidos
            </Button>
            <Button
              variant="none"
              className={`mode-button ${mode === 'custom' ? 'active' : ''}`}
              onClick={() => setMode('custom')}
              disabled={loading}
            >
              <FaPen />
              Monto personalizado
            </Button>
          </div>
        )}

        <div className="discount-content">
          {mode === 'preset' ? (
            // ========== PRESET DISCOUNT SELECTION ==========
            <div className="preset-discount-section">
              <div className="section-label">Selecciona un descuento</div>
              
              {/* Dropdown Select */}
              <Dropdown className="discount-dropdown-container">
                <Dropdown.Toggle 
                  variant="none" 
                  className="discount-dropdown"
                  disabled={loading}
                >
                  {selectedDiscount 
                    ? `${selectedDiscount.payment_discount_name} (${getDiscountValue(selectedDiscount)})`
                    : '-- Seleccionar descuento --'
                  }
                </Dropdown.Toggle>
                <Dropdown.Menu className="discount-dropdown-menu">
                  {manualDiscounts.map((discount) => (
                    <Dropdown.Item
                      key={discount.payment_discount_id}
                      active={selectedDiscount?.payment_discount_id === discount.payment_discount_id}
                      onClick={() => setSelectedDiscount(discount)}
                    >
                      <div className="discount-item">
                        {getScopeIcon(discount)}
                        <span className="discount-name">
                          {discount.payment_discount_name} ({getDiscountValue(discount)})
                        </span>
                      </div>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              {/* Selected Discount Preview */}
              {selectedDiscount && (
                <div className="selected-discount-preview">
                  <div className="preview-header">
                    {getScopeIcon(selectedDiscount)}
                    <span className="preview-name">{selectedDiscount.payment_discount_name}</span>
                    <span className="preview-value">{getDiscountValue(selectedDiscount)}</span>
                  </div>
                  <div className="preview-scope">
                    <span className="scope-label">Aplica a:</span> {getScopeDescription(selectedDiscount)}
                  </div>
                </div>
              )}

              {/* Optional comment for preset */}
              <div className="comment-row optional">
                <Form.Group className="comment-field">
                  <Form.Label className="comment-label">
                    Comentario <span className="optional-indicator">(opcional)</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={comment}
                    placeholder="Agrega un comentario si es necesario..."
                    onChange={(e) => setComment(e.target.value)}
                    disabled={loading}
                    maxLength={200}
                    className="comment-textarea"
                  />
                </Form.Group>
              </div>
            </div>
          ) : (
            // ========== CUSTOM DISCOUNT ENTRY ==========
            <>
              <div className="discount-type-toggle">
                <Button
                  variant="none"
                  className={`discount-type-button ${discountType === 'percentage' ? 'active' : ''}`}
                  onClick={() => setDiscountType('percentage')}
                  disabled={loading}
                >
                  Porcentaje <FaPercent />
                </Button>
                <Button
                  className={`discount-type-button ${discountType !== 'percentage' ? 'active' : ''}`}
                  variant="none"
                  onClick={() => setDiscountType('amount')}
                  disabled={loading}
                >
                  Monto <FaDollarSign />
                </Button>
              </div>

              {/* Row 1: Labels */}
              <div className="labels-row">
                <div className="input-label">
                  {discountType === 'percentage' ? 'Porcentaje de descuento' : 'Monto a descontar'}
                </div>
                <div className="preview-label">Vista previa</div>
              </div>

              {/* Row 2: Input and Preview Content */}
              <div className="content-row">
                <div className="discount-input-wrapper">
                  {discountType === 'percentage' ? (
                    <InputGroup className="input-group">
                      <Form.Control
                        type="text"
                        value={discountPercentage === 0 ? '' : discountPercentage}
                        placeholder="0"
                        onChange={(e) => {
                          let value = Number(e.target.value);
                          if (isNaN(value)) value = 0;
                          if (value < 0) value = 0;
                          if (value > 100) value = 100;
                          setDiscountPercentage(value);
                        }}
                        disabled={loading}
                      />
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                  ) : (
                    <InputGroup className="input-group">
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="text"
                        value={discountRawAmount === 0 ? '' : discountRawAmount}
                        placeholder="0"
                        onChange={(e) => {
                          let value = Number(e.target.value);
                          if (isNaN(value)) value = 0;
                          if (value < 0) value = 0;
                          if (value > totalCuenta) value = totalCuenta;
                          setDiscountRawAmount(value);
                        }}
                        disabled={loading}
                      />
                    </InputGroup>
                  )}
                </div>

                <div className="preview-calculation">
                  {discountType === 'percentage' && (
                    <>
                      <span className="preview-amount">
                        -{formatCurrency((discountPercentage / 100) * totalCuenta)}
                      </span>
                      <span className="arrow-separator">→</span>
                    </>
                  )}
                  <span className="final-amount">
                    {discountType === 'percentage'
                      ? formatCurrency(totalCuenta - (discountPercentage / 100) * totalCuenta)
                      : formatCurrency(totalCuenta - discountRawAmount)}
                  </span>
                </div>
              </div>

              {/* Row 3: Comment Field (Required for custom) */}
              <div className="comment-row">
                <Form.Group className="comment-field">
                  <Form.Label className="comment-label">
                    Motivo del descuento <span className="required-indicator">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={comment}
                    placeholder="Explica por qué se aplica este descuento..."
                    onChange={(e) => setComment(e.target.value)}
                    disabled={loading}
                    maxLength={200}
                    className="comment-textarea"
                  />
                  <div className="char-counter">{comment.length}/200 caracteres</div>
                </Form.Group>
              </div>
            </>
          )}

          {/* Row 4: Action Buttons */}
          <div className="discount-actions">
            <div className="primary-actions">
              <Button
                variant="none"
                className="action-button secondary"
                onClick={handleClose}
                disabled={loading}
              >
                <FaX />
                Cancelar
              </Button>
              <Button
                variant="none"
                className="action-button primary"
                onClick={applyDiscountToReceipt}
                disabled={
                  loading ||
                  (mode === 'preset'
                    ? !selectedDiscount
                    : !comment.trim() ||
                      (discountType === 'percentage' ? discountPercentage === 0 : discountRawAmount === 0))
                }
              >
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-1" />
                    Aplicando...
                  </>
                ) : (
                  <>
                    <FaCheck />
                    Aplicar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DiscountSection;
